/**
 * AppSelect - Componente reutilizable que envuelve react-select
 * con el estilo unificado de la aplicación (dark theme compatible).
 *
 * Props:
 * - options: array de { value, label } (requerido)
 * - value: valor actual (string o número)
 * - onChange: callback (value) => void — recibe solo el value, no el objeto completo
 * - placeholder: texto placeholder
 * - isSearchable: boolean (default true)
 * - isDisabled: boolean
 * - isClearable: boolean
 * - variant: "default" | "compact" | "modal" (estilo visual)
 * - menuPortalTarget: DOM element para renderizar el menú
 * - noOptionsMessage: string o función
 * - className: clase adicional
 * - id, name: atributos estándar
 */
import React from "react";
import Select from "react-select";
import "@/styles/ReactSelectDark.css";

const variantClassMap = {
  default: "",
  compact: "desktop-react-select",
  modal: "modal-react-select",
};

const AppSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Seleccionar...",
  isSearchable = true,
  isDisabled = false,
  isClearable = false,
  isMulti = false,
  variant = "default",
  menuPortalTarget,
  noOptionsMessage,
  className = "",
  id,
  name,
  ...rest
}) => {
  const selectedOption = isMulti
    ? options.filter(
        (o) =>
          Array.isArray(value) &&
          value.some((v) => String(o.value) === String(v)),
      )
    : options.find((o) => String(o.value) === String(value)) || null;

  const handleChange = (opt) => {
    if (onChange) {
      if (isMulti) {
        onChange(opt ? opt.map((o) => o.value) : []);
      } else {
        onChange(opt ? opt.value : "");
      }
    }
  };

  const baseStyles = {
    container: (base) => ({ ...base, width: "100%" }),
    menuPortal: (base) => ({ ...base, zIndex: 999999999 }),
    valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
    input: (base) => ({ ...base, margin: 0, padding: "2px 0" }),
  };

  return (
    <Select
      classNamePrefix="upload-react-select"
      className={`${variantClassMap[variant] || ""} ${className}`.trim()}
      options={options}
      value={selectedOption}
      onChange={handleChange}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isMulti={isMulti}
      placeholder={placeholder}
      menuPortalTarget={menuPortalTarget}
      styles={baseStyles}
      noOptionsMessage={
        noOptionsMessage
          ? typeof noOptionsMessage === "function"
            ? noOptionsMessage
            : () => noOptionsMessage
          : () => "Sin opciones"
      }
      inputId={id}
      name={name}
      {...rest}
    />
  );
};

export default AppSelect;
